import type { GeoMap } from "../model/GeoMap";

export default class UserSettingsControl {
    readonly MOST_RECENT_COUNT: number = 5;

    /**
     * Moves item with given id and value to front of array.
     *
     * e.g. var geoMaps = [{id: 101, ...}, {id: 102, ...}, {id: 103, ...}];
     *      moveItemToFrontOfArray("id", 102, geoMaps);
     *      =>  geoMaps = [{id: 102, ...}, {id: 101, ...}, {id: 103, ...}];
     * 
     * @param property 
     * @param val 
     * @param arr 
     */
    private moveItemToFrontOfArray(property: string | null, val: any, arr: any[]) {
        for (let i = 0; i < arr.length; i++) {
            if( (property && arr[i][property] === val) ||
                (arr[i] === val) ) {

                var a = arr.splice(i,1);   // remove the item
                arr.unshift(a[0]);         // add it back to the beginning
                break;
            }
        }
        // Matching item wasn't found. Array is unchanged
    }

    /**
     * Get all available maps, ordered firstly by 5 recently accessed maps (if available) and secondly by last modified.
     */
    getMapsByMostRecent(): Promise<GeoMap[]> {
        return Promise.all([
            es.api.geoMap.getWorkingCopyGeoMapList('OWNED_AND_SHARED', 'REGULAR'),
            es.api.userSettings.get()
        ]).then(([geoMaps, userSettings]) => {
            const nonPublicGeoMaps = geoMaps.owned.concat(geoMaps.shared);
            let allGeoMaps = nonPublicGeoMaps.concat(geoMaps['public']);

            allGeoMaps = _.sortBy(allGeoMaps, (gm) => gm.lastModified).reverse();
            for (let i = userSettings.lastViewedGeoMaps.length; i-- > 0; ) {
                this.moveItemToFrontOfArray("id", userSettings.lastViewedGeoMaps[i], allGeoMaps);
            }

            return this.cleanUpMostRecent(userSettings, "lastViewedGeoMaps", allGeoMaps).then(() => allGeoMaps);
        });
    }
    
    /**
     * Get all available territories, ordered firstly by 5 recently accessed territories (if available) and secondly by last modified.
     */
    getSchedulesByMostRecent(): Promise<GeoMap[]> {
        const getSchedulesByLastModified = (): JQueryDeferred<GeoMap[]> => {
            return es.api.geoMap.getWorkingCopyGeoMapList('OWNED_AND_SHARED', 'SCHEDULE').then((geoMaps) => {
                geoMaps = geoMaps.owned.concat(geoMaps.shared); // schedules are never public?
                geoMaps = _.sortBy(geoMaps, function(object){ return object.lastModified; }).reverse();

                return geoMaps;
            });
        };

        return Promise.all([
            getSchedulesByLastModified(),
            es.api.userSettings.get()
        ]).then(([geoMaps, userSettings]) => {
            for (let i = userSettings.lastViewedSchedules.length; i-- > 0; ) {
                this.moveItemToFrontOfArray("id", userSettings.lastViewedSchedules[i], geoMaps);
            }

            return this.cleanUpMostRecent(userSettings, "lastViewedSchedules", geoMaps).then(() => geoMaps);
        });
    };

    /**
     * Get all available territories, ordered firstly by 5 recently accessed territories (if available) and secondly by last modified.
     */
    getTerritoriesByMostRecent(): Promise<GeoMap[]> {
        const getTerritoriesByLastModified = (): JQueryDeferred<GeoMap[]> => {
            return es.api.geoMap.getWorkingCopyGeoMapList('OWNED_AND_SHARED', 'TERRITORY').then((geoMaps) => {
                geoMaps = geoMaps.owned.concat(geoMaps.shared); // territories are never public
                geoMaps = _.sortBy(geoMaps, (object) => object.lastModified).reverse();
    
                return geoMaps;
            });
        }

        return Promise.all([
            getTerritoriesByLastModified(),
            es.api.userSettings.get()
        ]).then(([geoMaps, userSettings]) => {
            for (let i = userSettings.lastViewedTerritories.length; i-- > 0; ) {
                this.moveItemToFrontOfArray("id", userSettings.lastViewedTerritories[i], geoMaps);
            }

            return this.cleanUpMostRecent(userSettings, "lastViewedTerritories", geoMaps).then(() => geoMaps);
        });
    }

    /**
     * 
     * @param id 
     * 
     * @returns 
     */
    updateMostRecentMaps(id: number) {
        return this.updateMostRecent("lastViewedGeoMaps", id);
    }

    /**
     * 
     * @param id 
     * 
     * @returns 
     */
    updateMostRecentSchedules(id: number) {
        return this.updateMostRecent("lastViewedSchedules", id);
    }
    
    /**
     * 
     * @param id 
     * 
     * @returns 
     */
    updateMostRecentTerritories(id: number) {
        return this.updateMostRecent("lastViewedTerritories", id);
    }

    /**
     * 
     * @param userSettings 
     * @param property 
     * @param id 
     * 
     * @returns 
     */
    private setUserSettingsMostRecent(userSettings, property: string, id: number) {
        return es.api.geoMap.get(id).then((geoMap) => {
            // if temp map is being added
            if (geoMap.$type.endsWith('TempGeoMap')) {
                // check if master copy is there
                var i = userSettings[property].indexOf(geoMap.parentId);
                if (i != -1) {
                    // and remove it
                    userSettings[property].splice(i, 1);
                }
            }

            // move item to front of array
            id = Number(id);
            if (userSettings[property].includes(id)) {
                this.moveItemToFrontOfArray(null, id, userSettings[property]);
            } else {
                userSettings[property].unshift(id);
            }

            userSettings[property] = userSettings[property].slice(0, this.MOST_RECENT_COUNT); // trim the array if necessary

            // update lastViewedMap while we're at it
            userSettings.lastViewedGeoMapId = id;

            return userSettings;
        });
    }

    /**
     * updates the most recent viewed with the given id (or ids). If it is an array, it's ordered from most recent to least recent.
     * 
     * @param property 
     * @param id 
     * 
     * @returns 
     */
    private updateMostRecent(property: string, id: number): JQueryDeferred<any> {
        if (es.isPublicAccess) {
            return es.util.deferred.resolved();
        }

        return es.api.userSettings.get().then((userSettings) => {
            if (!Array.isArray(id)) {
                return this.setUserSettingsMostRecent(userSettings, property, id);
            }

            let userSettingsDfd = es.util.deferred.resolved(userSettings);
            for (let i = id.length-1; i>=0; i--) {
                ((id) => {
                    userSettingsDfd = userSettingsDfd.then((userSettings) => {
                        return this.setUserSettingsMostRecent(userSettings, property, id)
                    });
                })(id[i]);
            }

            return userSettingsDfd;
        }).then(function(userSettings) {
            return es.api.userSettings.update(userSettings).done(function(userSettings){
                //console.log("updateMostRecent", property, id, userSettings);
                return userSettings;
            });
        });
    }

    /**
     * if the most recent list contains any items that are no longer available then remove them and update the list
     * 
     * @param userSettings 
     * @param property 
     * @param geoMaps 
     * 
     * @returns 
     */
    private cleanUpMostRecent(userSettings, property: string, geoMaps: GeoMap[]): JQueryDeferred<any> {
        let isUpdateRequired = false;

        //var missingIds = _.difference(userSettings[property], geoMaps.map(gm => gm.id));

        for (let i = userSettings[property].length; i-- > 0;) {
            if (geoMaps.get("id", userSettings[property][i]) == null) {
                isUpdateRequired = true;
                userSettings[property].splice(i, 1);
            }
        }

        if (userSettings[property].length > 0) {
            userSettings.lastViewedGeoMapId = userSettings[property][0];
        } else {
            userSettings.lastViewedGeoMapId = null;
        }

        if (isUpdateRequired) {
            //console.log("cleanUpMostRecent", missingIds, userSettings);
            return es.api.userSettings.update(userSettings);
        }

        return es.util.deferred.resolved(userSettings);
    }
}
